import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Lang } from '../types';
import { RootState } from '../store';
import {
  Event,
  EventCode,
  EventInput1,
  EventObjectType,
} from '../types/events';
import { submitEvent } from '../utils/httpRequest';
import exp from 'constants';

export const EVENTS = {
  conversationPause: {
    eventCode: EventCode.CV_PAUS,
    objType: EventObjectType.CONVERSATION,
  },
  conversationResume: {
    eventCode: EventCode.CV_RESU,
    objType: EventObjectType.CONVERSATION,
  },
  conversationEnd: {
    eventCode: EventCode.CV_END,
    objType: EventObjectType.CONVERSATION,
  },
  selectTemplate: {
    eventCode: EventCode.TP_SLCT,
    objType: EventObjectType.TEMPLATE,
  },
  selectIssue: {
    eventCode: EventCode.IS_SLCT,
    objType: EventObjectType.ISSUE,
  },
  rejectIssue: {
    eventCode: EventCode.IS_RJCT,
    objType: EventObjectType.ISSUE,
  },
  enterRoutine: {
    eventCode: EventCode.RT_ENTR,
    objType: EventObjectType.ROUTINE,
  },
  closeRoutine: {
    eventCode: EventCode.RT_CLSE,
    objType: EventObjectType.ROUTINE,
  },
  showStep: {
    eventCode: EventCode.ST_SHOW,
    objType: EventObjectType.STEP,
  },
};

export interface EventState {
  conversationId?: string | number;
  templateId?: string | number;
  routineId?: string | number;
  routineConversationId?: string | number;
  isDiagnoseConversationClosed?: boolean;
}

const initialState: EventState = {};

export const initDiagnoseConversation = createAsyncThunk(
  'event/initDiagnoseConversation',
  async (templateId: string | number) => {
    const response = await submitEvent({
      ...EVENTS.selectTemplate,
      objId: templateId,
    });

    return response?.data?.conversationid;
  }
);

export const closeDiagnoseConversation = createAsyncThunk(
  'event/closeDiagnoseConversation',
  async (
    {
      status,
      checkDuplicate,
    }: { status: EventInput1; checkDuplicate?: boolean },
    { getState }
  ) => {
    const {
      event: { conversationId, isDiagnoseConversationClosed },
    } = getState() as RootState;
    if (checkDuplicate && isDiagnoseConversationClosed) {
      return undefined;
    }
    if (!conversationId) {
      console.warn('Conversation id is not set');
      return undefined;
    }
    const response = await submitEvent({
      ...EVENTS.conversationEnd,
      conversationid: conversationId,
      objId: conversationId,
      input1: status,
    });

    return response?.data?.conversationid;
  }
);

export const initRoutineConversation = createAsyncThunk(
  'event/initRoutineConversation',
  async (routineId: string | number, { getState }) => {
    const {
      event: { conversationId },
    } = getState() as RootState;
    const response = await submitEvent({
      ...EVENTS.enterRoutine,
      conversationid: conversationId,
      objId: routineId,
    });

    return response?.data?.routineConversationid;
  }
);

export const closeRoutineConversation = createAsyncThunk(
  'event/closeRoutineConversation',
  async (
    { routineId, status }: { routineId?: string | number; status: EventInput1 },
    { getState }
  ) => {
    const {
      event: { routineConversationId },
    } = getState() as RootState;
    if (!routineConversationId) {
      console.warn('Routine conversation id is not set');
      return undefined;
    }
    const response = await submitEvent({
      ...EVENTS.closeRoutine,
      conversationid: routineConversationId,
      input1: status,
      objId: routineConversationId,
    });

    return response?.data;
  }
);

export const selectIssueEvent = createAsyncThunk(
  'event/selectIssueEvent',
  async (
    { issueId, selected }: { issueId: string | number; selected: 0 | 1 },
    { getState }
  ) => {
    const {
      event: { conversationId },
    } = getState() as RootState;
    const issueEvent = selected ? EVENTS.selectIssue : EVENTS.rejectIssue;
    const response = await submitEvent({
      ...issueEvent,
      conversationid: conversationId,
      objId: issueId,
    });

    return response?.data;
  }
);

export const switchStepEvent = createAsyncThunk(
  'event/switchStepEvent',
  async (
    {
      stepId,
      nextStep,
    }: { stepId: string | number; nextStep: string | number },
    { getState }
  ) => {
    const {
      event: { conversationId, routineId },
    } = getState() as RootState;
    const response = await submitEvent({
      ...EVENTS.showStep,
      conversationid: conversationId,
      objId: stepId,
      input1: nextStep as string,
      input2: routineId as string,
    });

    return response?.data;
  }
);

export const sendEvent = createAsyncThunk(
  'event/sendEvent',
  async (event: Event, { getState }) => {
    const {
      event: { conversationId },
    } = getState() as RootState;
    const response = await submitEvent({
      ...event,
      conversationid: conversationId,
    });

    return response?.data;
  }
);

export const eventSlice = createSlice({
  name: 'event',
  initialState,
  reducers: {
    setConversationId: (state, action: PayloadAction<string | number>) => {
      state.conversationId = action.payload;
    },
    setDiagnoseTemplateId: (state, action: PayloadAction<string | number>) => {
      state.templateId = action.payload;
    },
    setIsDiagnoseConversationClosed: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isDiagnoseConversationClosed = action.payload;
    },
    setRoutineConversationId: (
      state,
      action: PayloadAction<string | number>
    ) => {
      state.routineConversationId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendEvent.fulfilled, (state, action) => {
        // if (action.payload.routineConversationId) {
        //   state.routineConversationId = action.payload.routineConversationId;
        // }
      })
      .addCase(sendEvent.rejected, (state, action) => {
        console.warn(`Send Event failed: ${action.payload}`);
      });
    builder
      .addCase(initDiagnoseConversation.pending, (state, action) => {
        state.conversationId = undefined;
        state.isDiagnoseConversationClosed = false;
      })
      .addCase(initDiagnoseConversation.fulfilled, (state, action) => {
        state.conversationId = action.payload;
      })
      .addCase(initDiagnoseConversation.rejected, (state, action) => {
        console.warn(`Init diagnose conversion failed: ${action.payload}`);
      });
    builder
      .addCase(closeDiagnoseConversation.pending, (state, action) => {
        // state.conversationId = undefined;
        // state.templateId = undefined;
        if (!action.meta.arg.checkDuplicate) {
          state.isDiagnoseConversationClosed = true;
        }
      })
      .addCase(closeDiagnoseConversation.fulfilled, (state, action) => {
        state.conversationId = undefined;
        state.templateId = undefined;
        state.isDiagnoseConversationClosed = true;
      })
      .addCase(closeDiagnoseConversation.rejected, (state, action) => {
        console.warn(`Close diagnose conversion failed: ${action.payload}`);
      });
    builder
      .addCase(initRoutineConversation.pending, (state, action) => {
        state.routineId = action.meta.arg;
        state.routineConversationId = undefined;
      })
      .addCase(initRoutineConversation.fulfilled, (state, action) => {
        state.routineConversationId = action.payload;
      })
      .addCase(initRoutineConversation.rejected, (state, action) => {});
    builder
      .addCase(closeRoutineConversation.pending, (state, action) => {})
      .addCase(closeRoutineConversation.fulfilled, (state, action) => {
        state.routineConversationId = undefined;
      })
      .addCase(closeRoutineConversation.rejected, (state, action) => {
        console.warn(`Close routine conversion failed: ${action.payload}`);
      });
    builder.addCase(selectIssueEvent.rejected, (state, action) => {
      console.warn(`Select issue event failed: ${action.payload}`);
    });
    builder.addCase(switchStepEvent.rejected, (state, action) => {
      console.warn(`Switch Step event failed: ${action.payload}`);
    });
  },
});

export const {
  setConversationId,
  setRoutineConversationId,
  setDiagnoseTemplateId,
  setIsDiagnoseConversationClosed,
} = eventSlice.actions;

export default eventSlice.reducer;

export const selectConversationTemplateId = (state: RootState) =>
  state.event.templateId || [];

export const selectConversationId = (state: RootState) =>
  state.event.conversationId || [];

export const selectRoutineConversationId = (state: RootState) =>
  state.event.routineConversationId || [];

export const selectIsDiagnoseConversationClosed = (state: RootState) =>
  state.event.isDiagnoseConversationClosed || false;
